'use client';

import { Accordion } from '@base-ui/react/accordion';
import { css } from '@repo/pandacss/styled-system/css';
import { filter } from '@repo/pandacss/styled-system/recipes';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import { useState } from 'react';
import { MdCheck, MdExpandLess, MdExpandMore } from 'react-icons/md';
import { useShallow } from 'zustand/shallow';
import { useGeo } from '../../../GeoProvider/hook';
import { useFilterEvents } from '../../../OffersFilter/useFilterEvents';
import { useRipplePress } from '../../../OffersFilter/useRipplePress';
import { useFilterLocationOptions } from '../store/hooks';
import { useOffersFilterStore } from '../store/useOffersFilterStore';

const classes = filter();

// Location filter has special ripple effect styles not shared with other filters
const itemClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: 'full',
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingBlockStart: '4',
  paddingBlockEnd: '4',
  backgroundColor: '[transparent]',
  borderTopWidth: 'none',
  borderRightWidth: 'none',
  borderBottomWidth: 'none',
  borderLeftWidth: 'none',
  borderTopStyle: 'none',
  borderRightStyle: 'none',
  borderBottomStyle: 'none',
  borderLeftStyle: 'none',
  fontFamily: '[inherit]',
  fontSize: 'sm',
  color: 'neutral.dark',
  cursor: 'pointer',
  position: 'relative',
  overflowX: 'hidden',
  overflowY: 'hidden',
  _hover: { backgroundColor: 'neutral.lightest' },
  _after: {
    content: '""',
    display: 'block',
    position: 'absolute',
    width: 'full',
    height: 'full',
    top: '0',
    left: '0',
    pointerEvents: 'none',
    backgroundImage: '[radial-gradient(circle,token(colors.neutral.main) 10%,transparent 10.01%)]',
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: '[50%]',
    backgroundPositionY: '[50%]',
    // No resting `transition` here: the async Panda stylesheet applies these
    // properties over the already-painted (inline critical CSS) trigger, and a
    // resting transition would animate that change into a ripple on load.
    // The ripple is driven by the `ripple` keyframe, which useRipplePress runs
    // by toggling `data-rippling` on pointer/keyboard activation.
    transform: '[scale(0)]',
    opacity: '0',
  },
  '&[data-rippling]::after': {
    animationName: 'ripple',
    animationDuration: '[0.4s]',
    animationTimingFunction: 'ease-out',
  },
});

const LocationOption = ({
  label,
  value,
  selected,
  onSelect,
}: {
  label: string;
  value: string;
  selected: boolean;
  onSelect: (value: string) => void;
}) => {
  const { ref } = useRipplePress();
  return (
    <button ref={ref} type="button" className={itemClass} onClick={() => onSelect(value)}>
      <span>{label}</span>
      {selected && <MdCheck size={18} color="#008254" />}
    </button>
  );
};

export const MobileSelectLocation = ({
  actionSection,
  pageIdentifier,
}: {
  actionSection: string;
  pageIdentifier: string;
}) => {
  const locationOptions = useFilterLocationOptions();
  const { regionCode, setRegionCode } = useGeo();
  const { handleUpdateFilterFormState, handleApplyFilters, appliedFilters } = useOffersFilterStore(
    useShallow(state => ({
      handleUpdateFilterFormState: state.handleUpdateFilterFormState,
      handleApplyFilters: state.handleApplyFilters,
      appliedFilters: state.appliedFilters,
    })),
  );
  const { updateFiltersEvent } = useFilterEvents({
    actionSection,
    pageIdentifier,
    additionalAttributes: {},
  });
  const [isOpen, setIsOpen] = useState(false);

  const selectedLocation = appliedFilters?.selectedLocation ?? regionCode;

  const handleChange = (value: string) => {
    setTimeout(() => {
      handleUpdateFilterFormState({ selectedLocation: value });
      setRegionCode(value as StateAbbreviation);
      const newState = handleApplyFilters();
      if (!newState) return;
      updateFiltersEvent({
        additional_attributes: {
          location: value,
          product_types: newState.appliedFilters.selectedProductTypes,
        },
      });
    }, 100);
  };

  return (
    <Accordion.Root
      defaultValue={['']}
      onValueChange={value => setIsOpen(value.includes('LOCATION'))}
    >
      <Accordion.Item value="LOCATION">
        <Accordion.Header className={classes.accordionHeader}>
          <Accordion.Trigger className={classes.accordionTrigger}>
            <span className={classes.accordionTriggerLabel}>LOCATION</span>
            {isOpen ? (
              <MdExpandLess size={20} aria-hidden />
            ) : (
              <MdExpandMore size={20} aria-hidden />
            )}
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Panel className={classes.accordionPanel}>
          {locationOptions.map(({ label, value }) => (
            <LocationOption
              key={value}
              label={label}
              value={value}
              selected={selectedLocation === value}
              onSelect={handleChange}
            />
          ))}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion.Root>
  );
};
